import { Fragment } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import SoftTypography from "components/SoftTypography";
import { figmaTokens } from "assets/theme/designTokens";

function parseInline(text) {
  const parts = String(text ?? "").split(/(\*\*[^*]+\*\*|_[^_]+_|`[^`]+`)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <Box component="span" key={i} sx={{ fontWeight: 700, color: figmaTokens.textPrimary }}>
          {part.slice(2, -2)}
        </Box>
      );
    }
    if (part.startsWith("_") && part.endsWith("_") && part.length > 2) {
      return (
        <Box component="span" key={i} sx={{ fontStyle: "italic", color: figmaTokens.textSecondary }}>
          {part.slice(1, -1)}
        </Box>
      );
    }
    if (part.startsWith("`") && part.endsWith("`")) {
      return (
        <Box
          component="span"
          key={i}
          sx={{
            fontFamily: "monospace",
            fontSize: "0.78em",
            px: 0.5,
            py: 0.15,
            borderRadius: "4px",
            bgcolor: figmaTokens.pageBg,
            border: `1px solid ${figmaTokens.borderSubtle}`,
          }}
        >
          {part.slice(1, -1)}
        </Box>
      );
    }
    return <Fragment key={i}>{part}</Fragment>;
  });
}

function parseBlocks(text) {
  const lines = String(text ?? "").split("\n");
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      i += 1;
      continue;
    }

    const heading = trimmed.match(/^(#{1,3})\s+(.+)$/);
    if (heading) {
      blocks.push({ type: "heading", level: heading[1].length, text: heading[2] });
      i += 1;
      continue;
    }

    if (/^[-*]\s+/.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^[-*]\s+/, ""));
        i += 1;
      }
      blocks.push({ type: "ul", items });
      continue;
    }

    if (/^\d+\.\s+/.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+\.\s+/, ""));
        i += 1;
      }
      blocks.push({ type: "ol", items });
      continue;
    }

    const paraLines = [line];
    i += 1;
    while (i < lines.length && lines[i].trim() && !/^#{1,3}\s/.test(lines[i].trim()) && !/^[-*]\s+/.test(lines[i].trim()) && !/^\d+\.\s+/.test(lines[i].trim())) {
      paraLines.push(lines[i]);
      i += 1;
    }
    blocks.push({ type: "paragraph", text: paraLines.join("\n") });
  }

  return blocks;
}

const listSx = {
  m: 0,
  pl: 2.25,
  "& li": { mb: 0.5, lineHeight: 1.55 },
};

export function MarkdownContent({ text, isUser }) {
  const blocks = parseBlocks(text);
  const color = isUser ? "#fff" : figmaTokens.textBody;

  return (
    <Box sx={{ "& > *:first-of-type": { mt: 0 }, "& > *:last-child": { mb: 0 } }}>
      {blocks.map((block, idx) => {
        if (block.type === "heading") {
          const variant = block.level === 1 ? "h6" : block.level === 2 ? "button" : "caption";
          return (
            <SoftTypography
              key={idx}
              variant={variant}
              component="div"
              fontWeight="bold"
              sx={{ color, mt: idx > 0 ? 1.25 : 0, mb: 0.5, lineHeight: 1.4 }}
            >
              {parseInline(block.text)}
            </SoftTypography>
          );
        }

        if (block.type === "ul") {
          return (
            <Box component="ul" key={idx} sx={{ ...listSx, color, mt: idx > 0 ? 0.75 : 0 }}>
              {block.items.map((item, j) => (
                <Box component="li" key={j} sx={{ fontSize: "0.8125rem" }}>
                  {parseInline(item)}
                </Box>
              ))}
            </Box>
          );
        }

        if (block.type === "ol") {
          return (
            <Box component="ol" key={idx} sx={{ ...listSx, color, mt: idx > 0 ? 0.75 : 0 }}>
              {block.items.map((item, j) => (
                <Box component="li" key={j} sx={{ fontSize: "0.8125rem" }}>
                  {parseInline(item)}
                </Box>
              ))}
            </Box>
          );
        }

        return (
          <SoftTypography
            key={idx}
            variant="button"
            component="div"
            fontWeight="regular"
            sx={{ color, lineHeight: 1.6, whiteSpace: "pre-wrap", fontSize: "0.8125rem", mt: idx > 0 ? 0.75 : 0 }}
          >
            {parseInline(block.text)}
          </SoftTypography>
        );
      })}
    </Box>
  );
}

MarkdownContent.propTypes = {
  text: PropTypes.string,
  isUser: PropTypes.bool,
};
